import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { assert, expr } from '@codegrade/utils';
import Loader from '@/components/Loader';
// @ts-expect-error This component is not yet in TS
import SubmitButton from '@/components/SubmitButton';
import * as utils from '@/utils';
/**
 * Type guard that checks if is ISubmitButton interface.
 *
 * @param component The component instance to check.
 * @returns True if the component has a submit method.
 */
function isSubmitButton(component) {
    return component != null && typeof component.onClick === 'function';
}
/** A generic load more button that will render properly based on the state. */
export const LoadMoreButton = tsx.component({
    props: {
        /** The text that will be in the load more submitButton. */
        buttonText: p(String).default('Load More'),
        /** The state of the store. */
        state: p.ofType().required,
        /** Load the next page of the resource. */
        loadMore: p.ofFunction().required,
        runOnVisibile: p.ofType().default(false),
        loaderScale: p.ofType().default(1),
    },
    /** @returns The data of the component. */
    data() {
        return { triggeredLoading: false, triggeredLoadingIdx: 0, isVisible: false };
    },
    /** Called when the component is destroyed. */
    destroyed() {
        this.isVisible = false;
    },
    /** Called when the component is created. */
    created() {
        this.$watch(() => this.state.state, state => {
            if (state === 'not-started' || state === 'loaded-all') {
                this.setTriggeredLoading(false);
            }
        });
        this.$watch(() => this.isVisible, isVisible => {
            if (isVisible && this.runOnVisibile) {
                this.$withRef('btn', (btn) => {
                    if (isSubmitButton(btn)) {
                        btn.onClick();
                    }
                });
            }
        });
        this.$watch(() => this.state.state, value => {
            switch (value) {
                case 'loading':
                    if (!this.triggeredLoading) {
                        this.isVisible = false;
                    }
                    break;
                case 'loaded':
                    break;
                case 'loaded-all':
                case 'not-started':
                    this.isVisible = false;
                    break;
                default:
                    assert.AssertionError.assertNever(value);
            }
        });
    },
    methods: {
        /**
         * Sets the loading state for the button and increments the loading cycle index.
         * This is used to prevent race conditions with overlapping load calls.
         *
         * @param val The new loading state.
         * @returns The new loading cycle index.
         */
        setTriggeredLoading(val) {
            this.triggeredLoading = val;
            return ++this.triggeredLoadingIdx;
        },
        /**
         * An async function that repeatedly calls `loadMore` as long as the component
         * is visible and no new load cycles have been triggered.
         *
         * This enables the "infinite scroll" behavior where new pages are fetched
         * automatically when the button becomes visible in the viewport.
         */
        async submitFunction() {
            // Mark that we've started a load cycle, and capture the version.
            const before = this.setTriggeredLoading(true);
            try {
                let keepGoing;
                do {
                    // Load the next page.
                    // eslint-disable-next-line no-await-in-loop
                    await this.loadMore();
                    // If we're not auto-loading on visibility, bail out.
                    if (!this.runOnVisibile) {
                        break;
                    }
                    // Give Vue a tick so any v-visible updates can change this.isVisible.
                    // eslint-disable-next-line no-await-in-loop
                    await this.$afterRerender();
                    // Continue iff still visible *and* no newer load cycle has started.
                    keepGoing = this.isVisible && before === this.triggeredLoadingIdx;
                } while (keepGoing === true);
            }
            finally {
                // If a newer load cycle *did* start meanwhile, don't reset triggeredLoading,
                // otherwise, mark this cycle as complete.
                if (before === this.triggeredLoadingIdx) {
                    this.setTriggeredLoading(false);
                }
            }
        },
    },
    computed: {
        /**
         * @returns A callback function for the `v-visible` directive to update the
         * `isVisible` state, or `null` if auto-loading is disabled.
         */
        onVisibleCallback() {
            if (!this.runOnVisibile) {
                return null;
            }
            return (visible) => {
                this.isVisible = visible ?? false;
            };
        },
        /** @returns The button when we have loaded something, but might want to load more. */
        loadedNode() {
            return (<div class="load-more-button-wrapper d-flex justify-content-center">
                    <SubmitButton ref="btn" class="load-more-button my-2 mx-1" variant="secondary" waitAtLeast={0} duration={0} submit={this.submitFunction} v-visible={this.onVisibleCallback}>
                        {this.buttonText}
                    </SubmitButton>
                </div>);
        },
    },
    /** @returns The rendered button based on the state of the conversations. */
    render() {
        return expr.switcher(this.state.state, {
            'not-started': () => <Loader class="loader-not-started" scale={this.loaderScale}/>,
            loading: () => {
                if (this.triggeredLoading || this.state.data.length > 0) {
                    return this.loadedNode;
                }
                return <Loader class="loader-initial-load" scale={this.loaderScale}/>;
            },
            loaded: () => this.loadedNode,
            'loaded-all': () => utils.emptyVNode(),
        });
    },
});
